import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  EventAvailable as EventAvailableIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { get } from '../services/api.service';
import { SalarySlip, Attendance, AttendanceStatus } from '../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

interface SalarySlipResponse {
  salarySlips: SalarySlip[];
  summary: {
    count: number;
    totalNetPay: number;
    totalGross: number;
    totalDeductions: number;
  };
}

interface AttendanceResponse {
  attendance: Attendance[];
  stats: {
    totalRecords: number;
    present: number;
    absent: number;
    halfDay: number;
    leave: number;
    holiday: number;
  };
}

const getAttendanceColor = (status: AttendanceStatus): string => {
  switch (status) {
    case 'present':
      return '#10B981'; // Green
    case 'absent':
      return '#EF4444'; // Red
    case 'half_day':
      return '#F59E0B'; // Amber
    case 'leave':
      return '#3B82F6'; // Blue
    case 'holiday':
      return '#8B5CF6'; // Purple
    default:
      return '#6B7280'; // Gray
  }
};

const getStatusLabel = (status: AttendanceStatus): string => {
  switch (status) {
    case 'present':
      return 'Present';
    case 'absent':
      return 'Absent';
    case 'half_day':
      return 'Half Day';
    case 'leave':
      return 'Leave';
    case 'holiday':
      return 'Holiday';
    default:
      return status;
  }
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatMonth = (monthStr: string): string => {
  const [year, month] = monthStr.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleDateString('en-IN', {
    month: 'long',
    year: 'numeric',
  });
};

export function EmployeePortal() {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Salary Slips
  const [salarySlips, setSalarySlips] = useState<SalarySlip[]>([]);
  const [slipsSummary, setSlipsSummary] = useState({
    count: 0,
    totalNetPay: 0,
    totalGross: 0,
    totalDeductions: 0,
  });

  // Attendance
  const [attendance, setAttendance] = useState<Attendance[]>([]);
  const [attendanceStats, setAttendanceStats] = useState({
    totalRecords: 0,
    present: 0,
    absent: 0,
    halfDay: 0,
    leave: 0,
    holiday: 0,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load salary slips
      const slipsResponse = await get<SalarySlipResponse>('/salary/my-slips');
      setSalarySlips(slipsResponse.salarySlips);
      setSlipsSummary(slipsResponse.summary);

      // Load attendance (last 3 months)
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      const startDate = threeMonthsAgo.toISOString().split('T')[0];

      const attendanceResponse = await get<AttendanceResponse>(
        `/salary/my-attendance?startDate=${startDate}&limit=90`
      );
      setAttendance(attendanceResponse.attendance);
      setAttendanceStats(attendanceResponse.stats);
    } catch (err: any) {
      console.error('Error loading employee data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSlip = (slipId: string) => {
    // TODO: Implement PDF download
    console.log('Download slip:', slipId);
  };

  const handleViewSlip = (slipId: string) => {
    // TODO: Implement slip detail view
    console.log('View slip:', slipId);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Employee Portal
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Welcome, {user?.name}
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #667EEA 0%, #764BA2 100%)',
              color: 'white',
            }}
          >
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <DescriptionIcon sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Salary Slips
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {slipsSummary.count}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              color: 'white',
            }}
          >
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <EventAvailableIcon sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Present Days
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {attendanceStats.present}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
              color: 'white',
            }}
          >
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <CalendarIcon sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Leave Days
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {attendanceStats.leave}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
              color: 'white',
            }}
          >
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <DescriptionIcon sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Earned
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {formatCurrency(slipsSummary.totalNetPay)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ borderRadius: '16px', border: '1px solid #E5E7EB' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab label="Salary Slips" />
            <Tab label="Attendance" />
          </Tabs>
        </Box>

        {/* Salary Slips Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box px={3} pb={3}>
            {salarySlips.length === 0 ? (
              <Alert severity="info">No salary slips available yet.</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell>Gross Earnings</TableCell>
                      <TableCell>Deductions</TableCell>
                      <TableCell>Net Pay</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {salarySlips.map((slip) => (
                      <TableRow key={slip.salarySlipId}>
                        <TableCell>
                          <Typography fontWeight={600}>
                            {formatMonth(slip.month)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {formatCurrency(slip.grossEarnings)}
                        </TableCell>
                        <TableCell>
                          {formatCurrency(slip.totalDeductions)}
                        </TableCell>
                        <TableCell>
                          <Typography
                            fontWeight={700}
                            color="primary"
                          >
                            {formatCurrency(slip.netPay)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={slip.status.toUpperCase()}
                            size="small"
                            color={
                              slip.status === 'paid'
                                ? 'success'
                                : slip.status === 'sent'
                                ? 'info'
                                : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                              size="small"
                              startIcon={<VisibilityIcon />}
                              onClick={() => handleViewSlip(slip.salarySlipId)}
                            >
                              View
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DownloadIcon />}
                              onClick={() => handleDownloadSlip(slip.salarySlipId)}
                            >
                              Download
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>

        {/* Attendance Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box px={3} pb={3}>
            {/* Attendance Stats */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} sm={4} md={2}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={700} color="#10B981">
                      {attendanceStats.present}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Present
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={700} color="#EF4444">
                      {attendanceStats.absent}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Absent
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={700} color="#F59E0B">
                      {attendanceStats.halfDay}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Half Day
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={700} color="#3B82F6">
                      {attendanceStats.leave}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Leave
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={700} color="#8B5CF6">
                      {attendanceStats.holiday}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Holiday
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={700} color="#6B7280">
                      {attendanceStats.totalRecords}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Days
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Attendance Table */}
            {attendance.length === 0 ? (
              <Alert severity="info">No attendance records available yet.</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Check In</TableCell>
                      <TableCell>Check Out</TableCell>
                      <TableCell>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attendance.map((record) => (
                      <TableRow key={record.attendanceId}>
                        <TableCell>
                          <Typography fontWeight={600}>
                            {formatDate(record.date)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(record.status)}
                            size="small"
                            sx={{
                              backgroundColor: getAttendanceColor(record.status),
                              color: 'white',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {record.checkInTime || '-'}
                        </TableCell>
                        <TableCell>
                          {record.checkOutTime || '-'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {record.notes || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>
      </Paper>
    </Box>
  );
}

export default EmployeePortal;
